import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import SubPageHero from '@/components/SubPageHero';
import Button from '@/components/ui/Button';
import { CLIENTS } from '@/data/mockData';
import c from './Company.module.scss';
import x from './CompanyCertifications.module.scss';
import { GUIDE_ICON } from '@/components/GuideIcons';
const Ic = ({ n }: { n: string }) => <div className={x.cardIcon}>{GUIDE_ICON[n]}</div>; // [2026-10-08] 라이트 카드 라인아이콘

// ============================================================================
//  설치사례 — 주요 거래처·실적  (/cases/clients)
//  [2026-10-08] 기획서 v7 6-3. 대표제목(슬로건)·5섹션 순서·연결 이름을 문서 그대로.
//    순서: ①실적숫자 ②업종분포 ③지역분포 ④거래처 ⑤대표프로젝트 (+행동)
//    실데이터: ④거래처 로고(CLIENTS 32개). 준비중: 실적 집계수치·지역분포·대표프로젝트(검증 자료 대기).
//  라이트 테마(설치사례 신뢰 영역). ★/cases/:slug보다 먼저 라우팅해야 'clients'가 slug로 안 잡힘.
// ============================================================================

const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};
function Soon({ label }: { label: string }) {
  return (
    <div className={x.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

export default function CasesClients() {
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);

  return (
    <main>
      <PageLoader />
      <SubPageHero
        title="주요 거래처·실적"
        subtitle="전국 현장에서 검증된 EVS 기술"
        image="/images/company/subpage-hero.webp"
        crumbs={[{ label: 'HOME', to: '/' }, { label: '설치 사례', to: '/cases' }, { label: '주요 거래처' }]}
      />

      {/* ① 실적 숫자 → 집계 기준 */}
      <section className={c.section}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>TRACK RECORD</p>
            <h2 className={c.headline}>전국 현장에서 검증된 실적</h2>
            <p className={c.lead}>설치 사업장·배출구·단말 수량과 지역 현황을 기준일과 함께 공개합니다.</p>
            <div className={`${x.cardGrid} ${x.three}`} style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {['설치 사업장', '배출구', '설치 단말', '설치 지역'].map((k) => (
                <div key={k} className={x.card}><h3 style={{ color: '#8299ab', fontSize: 15 }}>{k}</h3><p style={{ fontSize: 22, fontWeight: 700, color: '#00AEEF' }}>준비중</p></div>
              ))}
            </div>
            <p className={x.note}>※ 검증된 누적 집계 수치와 기준일은 확정 후 게시합니다.</p>
          </Reveal>
        </div>
      </section>

      {/* ② 업종 분포 → 사례 */}
      <section className={`${c.section} ${c.alt}`}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>INDUSTRIES</p>
            <h2 className={c.headline}>업종 분포</h2>
            {/* [2026-11-02 사용자지시] 과장 교정: '반도체·디스플레이'는 v7(TABLE44 실제 업종분포=도장·금속·주물·화학·건설)·
                리뉴얼 기획서(§4 거래처·실적, 과장주의) 어디에도 없는 과장 표현이라 제거.
                실제 거래처 로고 구성(화학·소재·금속·주물·식품·건설)과 v7 실측 업종에 맞춰 재작성.
                아이콘 'monitor'는 globe로 렌더되는 버그라 함께 제거. 연관: [[evs-keco-approval-wording]] 과장금지 기조, mockData CLIENTS tagline. */}
            <p className={c.lead}>화학·금속·주물·도장·건설 등 다양한 산업 현장에 적용되었습니다.</p>
            <div className={x.cardGrid} style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {[{ l: '화학', i: 'droplet' }, { l: '금속·주물', i: 'factory' }, { l: '도장·표면처리', i: 'electro' }, { l: '건설', i: 'building' }].map((k) => (
                <div key={k.l} className={x.card}><Ic n={k.i} /><h3 style={{ fontSize: 17 }}>{k.l}</h3></div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ③ 지역 분포 → 사례 */}
      <section className={c.section}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>REGIONS</p>
            <h2 className={c.headline}>지역 분포</h2>
            <p className={c.lead}>수도권·충청·영남·호남 등 전국 권역별 설치 현황을 안내합니다.</p>
            <Soon label="권역별 설치 현황" />
          </Reveal>
        </div>
      </section>

      {/* ④ 거래처 → 고객 사례 */}
      <section className={`${c.section} ${c.alt}`}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>CLIENTS</p>
            <h2 className={c.headline}>{CLIENTS.title}</h2>
            <p className={c.lead}>{CLIENTS.tagline}</p>
            <div className={x.logoGrid}>
              {CLIENTS.items.map((cl) => (
                <div key={cl.name} className={x.logoCell}><img src={cl.src} alt={cl.name} loading="lazy" /></div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ⑤ 대표 프로젝트 → 상세 사례 */}
      <section className={c.section}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>PROJECTS</p>
            <h2 className={c.headline}>대표 프로젝트</h2>
            <p className={c.lead}>규모·제품·연도 중심의 대표 프로젝트 10건 내외를 소개합니다.</p>
            <Soon label="대표 프로젝트" />
            <a className={x.ghost} style={{ marginTop: 18 }} onClick={() => nav('/cases')}>설치사례 전체 보기</a>
          </Reveal>
        </div>
      </section>

      {/* 행동 → 납품·협력 문의 */}
      <section className={`${c.section} ${c.alt}`}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>CONTACT</p>
            <h2 className={c.headline}>납품·협력을 문의하세요</h2>
            <p className={c.lead}>수행 규모·레퍼런스 자료를 안내해 드립니다.</p>
            <div className={x.ctaRow}>
              <Button variant="pill" onClick={() => nav('/support/inquiry')}>납품·협력 문의</Button>
              <a className={x.ghost} onClick={() => nav('/support/consult')}>실시간 상담</a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
